import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  ArrowLeft, 
  Clock, 
  CheckCircle, 
  CreditCard, 
  XCircle,
  Calendar,
  Users,
  MapPin,
  MessageSquare,
  Euro,
  Info,
  Sparkles,
  TrendingUp,
  Search,
  ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import PaymentCheckoutModal from '@/components/PaymentCheckoutModal';
import CounterOfferModal from '@/components/CounterOfferModal';
import AISearchChat from '@/components/AISearchChat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const TravelerDashboard = () => {
  const { t } = useTranslation();
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [offers, setOffers] = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchCity, setSearchCity] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Payment state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  // Counter-offer state
  const [showCounterOfferModal, setShowCounterOfferModal] = useState(false);
  const [selectedOfferForCounter, setSelectedOfferForCounter] = useState(null);

  useEffect(() => {
    // Wait for auth context to load before checking authentication
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (!token || !userData) {
        navigate('/');
        return false;
      }
      
      try {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.role !== 'traveler') {
          navigate('/');
          return false;
        }
        return true;
      } catch {
        navigate('/');
        return false;
      }
    };
    
    if (checkAuth()) {
      fetchRequests();
    }
  }, [navigate]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch travel requests (from new marketplace system)
      const requestsResponse = await axios.get(
        `${BACKEND_URL}/api/travel_requests/my-requests`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequests(requestsResponse.data.requests || []);

      // Fetch offers received
      try {
        const offersResponse = await axios.get(
          `${BACKEND_URL}/api/offers/by-traveler`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setOffers(offersResponse.data.offers || []);
      } catch (offerError) {
        console.log('No offers found or error fetching offers');
        setOffers([]);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Errore nel caricamento delle richieste');
    } finally {
      setLoading(false);
    }
  };

  const searchAiSuggestions = async () => {
    if (!searchCity.trim()) {
      toast.error('Inserisci una città per la ricerca');
      return;
    }
    
    setSearchLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/matching/suggestions/for-traveler?city=${encodeURIComponent(searchCity)}&budget_max=200&guests=2`
      );
      setAiSuggestions(response.data.suggestions || []);
      if (response.data.suggestions?.length === 0) {
        toast.info('Nessuna offerta trovata per questa città');
      } else {
        toast.success(`Trovate ${response.data.suggestions.length} offerte!`);
      }
    } catch (error) {
      console.error('Error fetching AI suggestions:', error);
      toast.error('Errore nella ricerca');
    } finally {
      setSearchLoading(false);
    }
  };

  const getStatusBadge = (stato, paymentStatus) => {
    // Map stato to display status
    const statusMap = {
      'aperta': { label: 'In Attesa', icon: Clock, color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
      'in_lavorazione': { label: 'In Lavorazione', icon: Clock, color: 'bg-blue-100 text-blue-800 border-blue-300' },
      'confermata': paymentStatus === 'paid' 
        ? { label: 'Pagata', icon: CreditCard, color: 'bg-green-100 text-green-800 border-green-300' }
        : { label: 'Accettata', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
      'rifiutata': { label: 'Rifiutata', icon: XCircle, color: 'bg-red-100 text-red-800 border-red-300' },
      'cancellata': { label: 'Cancellata', icon: XCircle, color: 'bg-gray-100 text-gray-800 border-gray-300' },
      'chiusa': { label: 'Completata', icon: CheckCircle, color: 'bg-purple-100 text-purple-800 border-purple-300' }
    };

    const status = statusMap[stato] || statusMap['aperta'];
    const Icon = status.icon;

    return (
      <Badge className={`${status.color} flex items-center gap-1 px-3 py-1 border`}>
        <Icon className="w-3 h-3" />
        {status.label}
      </Badge>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const statusMap = {
      'pending': { label: 'Da Pagare', color: 'bg-orange-100 text-orange-800 border-orange-300' },
      'paid': { label: 'Pagato', color: 'bg-green-100 text-green-800 border-green-300' },
      'completed': { label: 'Completato', color: 'bg-purple-100 text-purple-800 border-purple-300' },
      'refunded': { label: 'Rimborsato', color: 'bg-gray-100 text-gray-800 border-gray-300' }
    };

    const status = statusMap[paymentStatus] || statusMap['pending'];

    return (
      <Badge className={`${status.color} flex items-center gap-1 px-3 py-1 border`}>
        <CreditCard className="w-3 h-3" />
        {status.label}
      </Badge>
    );
  };

  const filterRequests = (filterType) => {
    switch (filterType) {
      case 'pending':
        return requests.filter(r => r.stato === 'aperta' || r.stato === 'in_lavorazione');
      case 'accepted':
        return requests.filter(r => r.stato === 'confermata' && r.payment_status === 'pending');
      case 'paid':
        return requests.filter(r => r.payment_status === 'paid' || r.payment_status === 'completed');
      case 'completed':
        return requests.filter(r => r.stato === 'chiusa');
      case 'rejected':
        return requests.filter(r => r.stato === 'rifiutata' || r.stato === 'cancellata');
      default:
        return requests;
    }
  };

  const handlePayNow = (requestId) => {
    // Placeholder for Stripe integration
    toast.info('Integrazione Stripe in arrivo!', {
      description: 'Il sistema di pagamento sarà disponibile a breve.'
    });
  };

  const handleAcceptOffer = async (offerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${BACKEND_URL}/api/offers/${offerId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    toast.success('Offerta accettata! Ora puoi procedere al pagamento.');
      fetchRequests(); // Refresh data
    } catch (error) {
      console.error('Error accepting offer:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'accettare l\'offerta');
    }
  };

  const handlePayOffer = (offer) => {
    setSelectedPayment({
      transactionType: 'offer_payment',
      referenceId: offer.id,
      amount: offer.offered_price,
      description: `Prenotazione presso ${offer.property_name}`,
      propertyName: offer.property_name,
      details: {
        checkIn: offer.request_details?.check_in_date,
        checkOut: offer.request_details?.check_out_date,
        guests: offer.request_details?.guests
      }
    });
    setShowPaymentModal(true);
  };

  const handleCounterOffer = (offer) => {
    setSelectedOfferForCounter(offer);
    setShowCounterOfferModal(true);
  };

  const handleCounterOfferSuccess = (counterOffer) => {
    toast.success(`Contro-offerta di €${counterOffer.counter_price} inviata!`);
    fetchRequests(); // Refresh data
  };

  const handleRejectOffer = async (offerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/offers/${offerId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Offerta rifiutata');
      fetchRequests(); // Refresh data
    } catch (error) {
      console.error('Error rejecting offer:', error);
      toast.error(error.response?.data?.detail || 'Errore nel rifiutare l\'offerta');
    }
  };

  const getOffersForRequest = (requestId) => {
    return offers.filter(offer => offer.request_id === requestId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const RequestCard = ({ request }) => {
    // Determine if this is a new marketplace request or old bidirectional request
    const isMarketplaceRequest = !request.struttura;
    const requestOffers = getOffersForRequest(request.id);
    
    if (isMarketplaceRequest) {
      // New marketplace request format
      return (
        <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 border-slate-200">
          <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50 pb-3">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-emerald-600" />
                  {request.city}
                </CardTitle>
                <CardDescription className="mt-1">
                  Richiesta Marketplace
                </CardDescription>
              </div>
              <div className="text-right">
                <div className="text-xl font-black text-emerald-600">
                  €{request.budget_min} - €{request.budget_max}
                </div>
                <div className="text-xs text-slate-500">range/notte</div>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="pt-4 space-y-3">
            {/* Dates */}
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-600" />
                <span className="text-slate-600">Check-in:</span>
                <span className="font-semibold">{formatDate(request.check_in_date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-600" />
                <span className="text-slate-600">Check-out:</span>
                <span className="font-semibold">{formatDate(request.check_out_date)}</span>
              </div>
            </div>

            {/* Guests */}
            {request.guests && (
              <div className="flex items-center gap-2 text-sm">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-slate-600">Ospiti:</span>
                <span className="font-semibold">{request.guests}</span>
              </div>
            )}

            {/* Notes */}
            {request.notes && (
              <div className="text-sm bg-slate-50 p-2 rounded">
                <span className="text-slate-600">Note: </span>
                <span className="text-slate-900">{request.notes}</span>
              </div>
            )}

            {/* Offers Received Section */}
            {(() => {
              if (requestOffers.length > 0) {
                return (
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4 mt-4">
                    <h4 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                      <Euro className="w-5 h-5 text-emerald-600" />
                      {requestOffers.length} {requestOffers.length === 1 ? 'Offerta Ricevuta' : 'Offerte Ricevute'}
                    </h4>
                    <div className="space-y-3">
                      {requestOffers.map((offer) => (
                        <div
                          key={offer.id}
                          className="bg-white border border-emerald-200 rounded-lg p-3 space-y-2"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-bold text-slate-900">{offer.property_name}</p>
                              <p className="text-sm text-slate-600">{offer.property_owner?.full_name || 'Property Owner'}</p>
                              {offer.room_description && (
                                <p className="text-xs text-slate-600 mt-1">{offer.room_description}</p>
                              )}
                              {offer.notes && (
                                <p className="text-xs text-slate-600 mt-1 italic">"{offer.notes}"</p>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-black text-emerald-600">€{offer.offered_price}</div>
                              <div className="text-xs text-slate-500">per notte</div>
                            </div>
                          </div>

                          {/* Offer Status Badge */}
                          <div className="flex items-center justify-between">
                            <Badge className={`
                              ${offer.status === 'pending' ? 'bg-amber-100 text-amber-800 border-amber-300' : ''}
                              ${offer.status === 'accepted' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : ''}
                              ${offer.status === 'counter_accepted' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : ''}
                              ${offer.status === 'rejected' ? 'bg-red-100 text-red-800 border-red-300' : ''}
                              ${offer.status === 'countered' ? 'bg-purple-100 text-purple-800 border-purple-300' : ''}
                              ${offer.status === 'paid' ? 'bg-green-100 text-green-800 border-green-300' : ''}
                              px-2 py-1 text-xs flex items-center gap-1
                            `}>
                              {offer.status === 'pending' && <><Clock className="w-3 h-3" /> In Attesa</>}
                              {offer.status === 'accepted' && <><CheckCircle className="w-3 h-3" /> Accettata</>}
                              {offer.status === 'counter_accepted' && <><CheckCircle className="w-3 h-3" /> Accordo Raggiunto</>}
                              {offer.status === 'rejected' && <><XCircle className="w-3 h-3" /> Rifiutata</>}
                              {offer.status === 'countered' && <><MessageSquare className="w-3 h-3" /> Contro-Offerta Inviata</>}
                              {offer.status === 'paid' && <><CreditCard className="w-3 h-3" /> Pagata</>}
                            </Badge>

                            {/* Accept/Reject/Counter Buttons */}
                            {offer.status === 'pending' && (
                              <div className="flex gap-2 flex-wrap">
                                <Button
                                  size="sm"
                                  onClick={() => handleAcceptOffer(offer.id)}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Accetta
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleCounterOffer(offer)}
                                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                                >
                                  <MessageSquare className="w-3 h-3 mr-1" />
                                  Contro-Offerta
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRejectOffer(offer.id)}
                                  className="border-red-300 text-red-700 hover:bg-red-50"
                                >
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Rifiuta
                                </Button>
                              </div>
                            )}

                            {/* Pay Now Button for accepted offers */}
                            {(offer.status === 'accepted' || offer.status === 'counter_accepted') && (
                              <Button
                                size="sm"
                                onClick={() => handlePayOffer(offer)}
                                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                              >
                                <CreditCard className="w-4 h-4 mr-1" />
                                Paga Ora €{offer.offered_price}
                              </Button>
                            )}

                            {/* Show waiting message for countered offers */}
                            {offer.status === 'countered' && (
                              <span className="text-sm text-purple-600 italic">
                                In attesa di risposta del proprietario...
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </CardContent>
        </Card>
      );
    }

    // Old bidirectional request format
    const isPaymentReady = request.stato === 'confermata' && request.payment_status === 'pending';

    return (
      <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 border-slate-200">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 pb-3">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-lg font-bold text-slate-900">
                {request.struttura.nome}
              </CardTitle>
              <CardDescription className="flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3" />
                {request.struttura.city}
              </CardDescription>
            </div>
            <div className="flex flex-col gap-2 items-end">
              {getStatusBadge(request.stato, request.payment_status)}
              {request.total_amount && (
                <div className="text-right">
                  <div className="text-2xl font-bold text-slate-900">
                    €{request.total_amount.toFixed(2)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-4 space-y-3">
          {/* Dates */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-slate-600">Check-in:</span>
              <span className="font-semibold">{formatDate(request.data_checkin)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-slate-600">Check-out:</span>
              <span className="font-semibold">{formatDate(request.data_checkout)}</span>
            </div>
          </div>

          {/* Guests */}
          {request.numero_ospiti && (
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-blue-600" />
              <span className="text-slate-600">Ospiti:</span>
              <span className="font-semibold">{request.numero_ospiti}</span>
            </div>
          )}

          {/* Unread Messages */}
          {request.messaggi_non_letti > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <MessageSquare className="w-4 h-4 text-orange-600" />
              <Badge className="bg-orange-100 text-orange-800 border-orange-300">
                {request.messaggi_non_letti} {request.messaggi_non_letti === 1 ? 'nuovo messaggio' : 'nuovi messaggi'}
              </Badge>
            </div>
          )}

          {/* Payment Info */}
          {request.total_amount && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 mt-0.5" />
                <div className="text-xs text-blue-900 space-y-1">
                  <div className="flex justify-between">
                    <span>Importo totale:</span>
                    <span className="font-semibold">€{request.total_amount.toFixed(2)}</span>
                  </div>
                  {request.platform_fee && (
                    <div className="flex justify-between text-blue-700">
                      <span>Commissione piattaforma (20%):</span>
                      <span>€{request.platform_fee.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            {isPaymentReady && (
              <Button
                onClick={() => handlePayNow(request.id)}
                className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 shadow-lg shadow-green-500/30"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Paga Ora
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => toast.info('Funzionalità messaggi in arrivo')}
              className="flex-1"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Messaggi
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Caricamento richieste...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="hover:bg-slate-100"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Torna al Marketplace
              </Button>
              <div className="h-8 w-px bg-slate-300"></div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900">
                  Le Mie Richieste
                </h1>
                <p className="text-sm text-slate-600 mt-1">
                  Gestisci le tue prenotazioni e pagamenti
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-600">Benvenuto,</p>
              <p className="text-lg font-semibold text-slate-900">{user?.full_name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* AI Search Section */}
        <Card className="mb-8 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900">Trova Offerte con AI</h3>
                <p className="text-sm text-slate-600">Lascia che l'intelligenza artificiale trovi le migliori offerte per te</p>
              </div>
              <Badge className="ml-auto bg-purple-100 text-purple-800 border-purple-300">GPT-5.2</Badge>
            </div>
            
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input 
                  placeholder="Inserisci una città (es: Roma, Milano, Firenze...)"
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchAiSuggestions()}
                  className="pl-10 h-12 text-lg border-purple-200 focus:border-purple-500"
                />
              </div>
              <Button 
                onClick={searchAiSuggestions}
                disabled={searchLoading}
                className="h-12 px-8 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              >
                {searchLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Cerca con AI
                  </>
                )}
              </Button>
            </div>

            {/* AI Results */}
            {aiSuggestions.length > 0 && (
              <div className="mt-6 space-y-4">
                <h4 className="font-semibold text-slate-900">Offerte Trovate dall'AI:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {aiSuggestions.map((suggestion) => (
                    <Card key={suggestion.id} className="bg-white hover:shadow-lg transition-all border-purple-200">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h5 className="font-bold text-slate-900">{suggestion.title}</h5>
                            <p className="text-sm text-slate-600">{suggestion.description}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-black text-purple-600">{suggestion.match_score}%</div>
                            <Badge className={`text-xs
                              ${suggestion.urgency === 'high' ? 'bg-red-100 text-red-800' : ''}
                              ${suggestion.urgency === 'medium' ? 'bg-orange-100 text-orange-800' : ''}
                              ${suggestion.urgency === 'low' ? 'bg-blue-100 text-blue-800' : ''}
                            `}>
                            {suggestion.urgency === 'high' ? 'Top Match' : suggestion.urgency === 'medium' ? 'Buono' : 'Match'}
                            </Badge>
                          </div>
                        </div>
                        
                        {/* Reasons */}
                        <div className="bg-purple-50 rounded-lg p-2 mb-3">
                          <ul className="space-y-1">
                            {suggestion.reasons?.slice(0, 2).map((reason, i) => (
                              <li key={i} className="flex items-start gap-1 text-xs text-purple-800">
                                <TrendingUp className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                <span>{reason}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Deal Info */}
                        {suggestion.deal && (
                          <div className="text-sm text-slate-600 mb-3">
                            <span className="font-semibold text-emerald-600">€{suggestion.deal.price_per_night}/notte</span>
                            {suggestion.deal.discount_percent > 0 && (
                              <Badge className="ml-2 bg-emerald-100 text-emerald-800">-{suggestion.deal.discount_percent}%</Badge>
                            )}
                          </div>
                        )}

                        <Button 
                          className="w-full bg-purple-600 hover:bg-purple-700"
                          onClick={() => navigate('/')}
                        >
                          {suggestion.action_label || "Visualizza Offerta"}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6 mb-8 bg-white shadow-sm">
            <TabsTrigger value="all" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Tutte ({requests.length})
            </TabsTrigger>
            <TabsTrigger value="pending" className="data-[state=active]:bg-yellow-600 data-[state=active]:text-white">
              In Attesa ({filterRequests('pending').length})
            </TabsTrigger>
            <TabsTrigger value="accepted" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              Accettate ({filterRequests('accepted').length})
            </TabsTrigger>
            <TabsTrigger value="paid" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
              Pagate ({filterRequests('paid').length})
            </TabsTrigger>
            <TabsTrigger value="completed" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              Completate ({filterRequests('completed').length})
            </TabsTrigger>
            <TabsTrigger value="rejected" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
              Rifiutate ({filterRequests('rejected').length})
            </TabsTrigger>
          </TabsList>

          {['all', 'pending', 'accepted', 'paid', 'completed', 'rejected'].map((tab) => (
            <TabsContent key={tab} value={tab} className="mt-6">
              {filterRequests(tab).length === 0 ? (
                <div className="text-center py-16">
                  <div className="bg-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 shadow-md">
                    <MessageSquare className="w-10 h-10 text-slate-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    Nessuna richiesta trovata
                  </h3>
                  <p className="text-slate-600">
                    {tab === 'all' 
                      ? 'Non hai ancora inviato richieste.'
                      : 'Non ci sono richieste in questa categoria.'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filterRequests(tab).map((request) => (
                    <RequestCard key={request.id} request={request} />
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Payment Checkout Modal */}
      {selectedPayment && (
        <PaymentCheckoutModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedPayment(null);
          }}
          transactionType={selectedPayment.transactionType}
          referenceId={selectedPayment.referenceId}
          amount={selectedPayment.amount}
          description={selectedPayment.description}
          propertyName={selectedPayment.propertyName}
          details={selectedPayment.details}
        />
      )}

      {/* Counter-Offer Modal */}
      {selectedOfferForCounter && (
        <CounterOfferModal
          isOpen={showCounterOfferModal}
          onClose={() => {
            setShowCounterOfferModal(false);
            setSelectedOfferForCounter(null);
          }}
          offer={selectedOfferForCounter}
          onSuccess={handleCounterOfferSuccess}
        />
      )}
    </div>
  );
};

export default TravelerDashboard;
